import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import { useRouter } from 'next/router';

import { routes } from '~/shared/constants/routes';

import { useToast } from '~/shared/hooks/useToast';

import { ICustomRadioSelectorOption } from '~/shared/components/Form/CustomRadioSelector/types';

import { passwordRecoveryMethodsOptions } from '~/modules/auth/assets/data/passwordRecoveryMethodsOptions';

import { unmask } from '~/shared/utils/masks';

import {
  resetUserPassword,
  sendUserPasswordRecoveryCode,
  validateUserRecoveryCode,
} from '~/modules/auth/services/passwordRecovery';

import { FCWithChildren } from '~/shared/types/FCWithChildren';
import { TSendUserPasswordRecoveryCodeMethod } from '../interfaces/ISendUserPasswordRecoveryCodeRequest';
import { IPasswordRecoveryFormData } from '../interfaces/IPasswordRecoveryFormData';

interface IPasswordRecoveryContextData {
  currentStep: number;
  nextStep(data?: Partial<IPasswordRecoveryFormData>): void;
  sendRecoveryCode(
    data: Pick<IPasswordRecoveryFormData, 'value'>
  ): Promise<void>;
  validateRecoveryCode(
    data: Pick<IPasswordRecoveryFormData, 'code'>
  ): Promise<void>;
  sendRecoveryCodeError: string;
  validateRecoveryCodeError: string;
  resetPassword(
    data: Pick<
      IPasswordRecoveryFormData,
      'newPassword' | 'newPasswordConfirmation'
    >
  ): Promise<void>;
  previousStep(): void;
  selectedMethod: TSendUserPasswordRecoveryCodeMethod;
  selectedMethodOption: ICustomRadioSelectorOption<TSendUserPasswordRecoveryCodeMethod>;
  recoveryCodeIsValidating: boolean;
  changeMethod(): void;
}

const PasswordRecoveryContext = createContext(
  {} as IPasswordRecoveryContextData
);

const PasswordRecoveryProvider: FCWithChildren = ({ children }) => {
  const toast = useToast();
  const router = useRouter();

  const formData = useRef({} as IPasswordRecoveryFormData);

  const [currentStep, setCurrentStep] = useState(1);
  const [sendRecoveryCodeError, setSendRecoveryCodeError] =
    useState<string>(undefined);
  const [validateRecoveryCodeError, setValidateRecoveryCodeError] =
    useState<string>(undefined);
  const [recoveryCodeIsValidating, setRecoveryCodeIsValidating] =
    useState(false);

  const setFormData = useCallback(
    (data: Partial<IPasswordRecoveryFormData>) => {
      const newFormData = {
        ...formData.current,
        ...(data || {}),
      };

      formData.current = newFormData;

      return newFormData;
    },
    []
  );

  const getFormData = useCallback(
    (field: keyof IPasswordRecoveryFormData): any => {
      return formData.current[field];
    },
    []
  );

  const nextStep = useCallback(
    (data?: Partial<IPasswordRecoveryFormData>) => {
      setCurrentStep((prevState) => prevState + 1);
      setFormData(data);
    },
    [setFormData]
  );

  const previousStep = useCallback(() => {
    setCurrentStep((prevState) => prevState - 1);
  }, []);

  const sendRecoveryCode = useCallback(
    async (data: Pick<IPasswordRecoveryFormData, 'value'>) => {
      try {
        setSendRecoveryCodeError('');

        const newFormData = setFormData(data);

        await sendUserPasswordRecoveryCode({
          method: newFormData.method,
          value:
            newFormData.method === 'email'
              ? newFormData.value
              : unmask(newFormData.value),
        });

        nextStep();
      } catch (error) {
        if (error?.response?.data?.tag === 'USER_NOT_FOUND') {
          setSendRecoveryCodeError('Nenhuma conta encontrada');
          return;
        }

        toast.show({
          title: 'Ops, não foi possível enviar o código de recuperação!',
          description:
            error?.response?.data?.displayMessage ||
            error?.response?.data?.message ||
            'Recarregue a página e tente novamente.',
          variant: 'error',
        });
      }
    },
    [nextStep, setFormData, toast]
  );

  const validateRecoveryCode = useCallback(
    async (data: Pick<IPasswordRecoveryFormData, 'code'>) => {
      try {
        setValidateRecoveryCodeError('');
        setRecoveryCodeIsValidating(true);

        const newFormData = setFormData(data);

        const userExists = await validateUserRecoveryCode(
          newFormData.code.toUpperCase()
        );

        if (!userExists) {
          setValidateRecoveryCodeError('Código de recuperação inválido');
          return;
        }

        nextStep();
      } catch (error) {
        toast.show({
          title: 'Ops, não foi possível verificar esse código de recuperação!',
          description:
            error?.response?.data?.displayMessage ||
            error?.response?.data?.message ||
            'Recarregue a página e tente novamente.',
          variant: 'error',
        });
      } finally {
        setRecoveryCodeIsValidating(false);
      }
    },
    [nextStep, setFormData, toast]
  );

  const resetPassword = useCallback(
    async (
      data: Pick<
        IPasswordRecoveryFormData,
        'newPassword' | 'newPasswordConfirmation'
      >
    ) => {
      try {
        const newFormData = setFormData(data);

        await resetUserPassword({
          newPassword: data.newPassword,
          passwordRecoveryCode: newFormData.code.toUpperCase(),
        });

        toast.show({
          title: 'Boa, sua senha foi alterada com sucesso!',
          description: 'Faça o seu login para entrar no portal.',
          variant: 'success',
        });

        router.push(routes.AUTH.SIGN_IN);
      } catch (error) {
        toast.show({
          title: 'Ops, não foi possível redefinir a sua senha!',
          description:
            error?.response?.data?.displayMessage ||
            error?.response?.data?.message ||
            'Recarregue a página e tente novamente.',
          variant: 'error',
        });
      }
    },
    [router, setFormData, toast]
  );

  const changeMethod = useCallback(() => {
    setCurrentStep(1);
  }, []);

  const selectedMethod = getFormData('method');

  const selectedMethodOption = useMemo(() => {
    return passwordRecoveryMethodsOptions.find(
      (item) => item.value === selectedMethod
    );
  }, [selectedMethod]);

  const contextData: IPasswordRecoveryContextData = useMemo(
    () => ({
      currentStep,
      nextStep,
      previousStep,
      sendRecoveryCode,
      validateRecoveryCode,
      resetPassword,
      changeMethod,
      sendRecoveryCodeError,
      recoveryCodeIsValidating,
      validateRecoveryCodeError,
      selectedMethod,
      selectedMethodOption,
    }),
    [
      currentStep,
      nextStep,
      previousStep,
      sendRecoveryCode,
      validateRecoveryCode,
      resetPassword,
      changeMethod,
      sendRecoveryCodeError,
      recoveryCodeIsValidating,
      validateRecoveryCodeError,
      selectedMethod,
      selectedMethodOption,
    ]
  );

  return (
    <PasswordRecoveryContext.Provider value={contextData}>
      {children}
    </PasswordRecoveryContext.Provider>
  );
};

const usePasswordRecovery = (): IPasswordRecoveryContextData => {
  return useContext(PasswordRecoveryContext);
};

export { PasswordRecoveryProvider, usePasswordRecovery };
